import type { CSSProperties, ReactNode } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { normalizeJobDescriptionMarkdown } from '@tabbio/utils/job-description';

export type JobScreeningQuestion = {
  id: string;
  prompt: string;
};

export type JobDetailSurfaceJob = {
  id: string;
  title: string;
  company: {
    name: string;
    logo?: string | null;
    slug?: string | null;
    /**
     * The employer's public Tabbio company page. When present the company
     * name in the header is a real `<a href>` to it, so every open job page
     * hands its employer's page an internal link (on 5 Sep 2026 no page on
     * www linked to any company page). Omit it when there is no indexable
     * company page to point at: a confidential employer, an external posting,
     * a company that opted out of search, or a handle that now belongs to a
     * person.
     */
    href?: string | null;
  };
  location?: string | null;
  type?: string | null;
  workMode?: string | null;
  level?: string | null;
  sourceLabel?: string | null;
  sourceIcon?: ReactNode;
  applyModeLabel?: string | null;
  postedLabel?: string | null;
  description: string;
  requirements: string[];
  tags?: string[];
  screeningQuestions?: JobScreeningQuestion[];
};

export type JobDetailSurfaceLabels = {
  locationFallback: string;
  description: string;
  requirements: string;
  screeningQuestions: string;
  tags: string;
};

export type JobDetailSurfaceProps = {
  job: JobDetailSurfaceJob;
  labels?: Partial<JobDetailSurfaceLabels>;
  statusSlot?: ReactNode;
  matchSlot?: ReactNode;
  actionSlot?: ReactNode;
  afterActionSlot?: ReactNode;
  maxWidth?: number;
  className?: string;
  style?: CSSProperties;
};

const DEFAULT_LABELS: JobDetailSurfaceLabels = {
  locationFallback: 'Location flexible',
  description: 'Description',
  requirements: 'Requirements',
  screeningQuestions: 'Screening questions',
  tags: 'Tags',
};

const TEXT = 'var(--tabbio-text, #0d0d0d)';
const TEXT_SECONDARY = 'var(--tabbio-text-secondary, #5d5d5d)';
const TEXT_TERTIARY = 'var(--tabbio-text-tertiary, #8f8f8f)';
const BORDER_SUBTLE = 'var(--tabbio-border-subtle, rgba(0,0,0,0.05))';
const BORDER = 'var(--tabbio-border, rgba(0,0,0,0.10))';
const SURFACE = 'var(--tabbio-surface, #f9f9f9)';
const SURFACE_SECONDARY = 'var(--tabbio-surface-secondary, #ededed)';
const SURFACE_ELEVATED = 'var(--tabbio-surface-elevated, #ffffff)';

export function JobSuitcaseIcon({
  size = 24,
  className,
  style,
}: {
  size?: number;
  className?: string;
  style?: CSSProperties;
}) {
  return (
    <svg
      aria-hidden="true"
      className={className}
      viewBox="0 0 24 24"
      width={size}
      height={size}
      color="currentColor"
      fill="none"
      stroke="currentColor"
      strokeWidth={2}
      strokeLinecap="round"
      strokeLinejoin="round"
      style={style}
    >
      <path d="M8.5 6.5C8.5 5.09554 8.5 4.39331 8.83706 3.88886C8.98298 3.67048 9.17048 3.48298 9.38886 3.33706C9.89331 3 10.5955 3 12 3C13.4045 3 14.1067 3 14.6111 3.33706C14.8295 3.48298 15.017 3.67048 15.1629 3.88886C15.5 4.39331 15.5 5.09554 15.5 6.5" />
      <path d="M22 14V13.5C22 10.2002 22 8.55025 20.9749 7.52513C19.9497 6.5 18.2998 6.5 15 6.5H9C5.70017 6.5 4.05025 6.5 3.02513 7.52513C2 8.55025 2 10.2002 2 13.5V14C2 17.2998 2 18.9497 3.02513 19.9749C4.05025 21 5.70017 21 9 21H15C18.2998 21 19.9497 21 20.9749 19.9749C22 18.9497 22 17.2998 22 14Z" />
      <path d="M13.5 14C19.5735 13.5094 21.75 10 21.75 10M2.25 10C2.25 10 4.42655 13.5094 10.5 14" />
      <path d="M10.5 14V13C10.5 12.7239 10.7239 12.5 11 12.5H13C13.2761 12.5 13.5 12.7239 13.5 13V14C13.5 14.8284 12.8284 15.5 12 15.5C11.1716 15.5 10.5 14.8284 10.5 14Z" />
    </svg>
  );
}

function humanizeJobValue(value: string) {
  return value
    .replace(/[_-]+/g, ' ')
    .replace(/\b\w/g, (char) => char.toUpperCase());
}

/**
 * The complete set of v1 tag namespaces. `buildJobRequirements` in
 * apps/api (`jobs.transforms.ts:157`) is the ONLY producer of tag-encoded
 * requirements and emits exactly these two — `jobs.service.ts:1210` documents
 * that they never enter v2 rows at all. Everything else that writes
 * `Job.requirements` (the AI wizard, the admin recruiting console, the legacy
 * Mongo import, the extension) stores free text.
 *
 * This is an exact allowlist, not a shape heuristic, because the shape of a
 * tag is also the shape of ordinary prose: the previous
 * `^[A-Za-z0-9]+(?:[:_-][A-Za-z0-9]+)*$` matched a bare `09:00` (rendered as
 * "00") and every single-token hyphenated requirement an employer might
 * plausibly type — `Self-motivated` → "Self Motivated", and the same for
 * `Detail-oriented`, `Full-time`, `Arabic-speaking`.
 *
 * Adding a namespace here is the deliberate act of opting its values into
 * unslug + Title Case; anything not listed renders verbatim.
 */
const TAG_ENCODED_REQUIREMENT_NAMESPACES = [
  'years_experience',
  'qualification',
] as const;

/**
 * `years_experience:3_5`, `qualification:bachelors`. The value is a
 * lowercase slug: the producers build it from an enum, so a capital letter or
 * any character outside `[a-z0-9_-]` means this is prose that merely happens to
 * start with one of the namespace words.
 */
const TAG_ENCODED_REQUIREMENT = new RegExp(
  `^(?:${TAG_ENCODED_REQUIREMENT_NAMESPACES.join('|')}):[a-z0-9]+(?:[_-][a-z0-9]+)*$`,
);

/**
 * Requirements come in two shapes and only one of them may be humanized.
 *
 * v1 native requirements are tag-encoded (`years_experience:3_5`) and keep
 * their historical treatment: drop the namespace, unslug, Title Case.
 *
 * Everything an employer or the AI job wizard writes is free text and MUST
 * render verbatim. Humanizing it deleted every character before the last colon
 * ("Experience: 5+ years building ROS2 systems" → " 5+ Years Building ROS2
 * Systems"), Title-Cased mid-sentence words ("Bachelor'S Degree") and turned
 * hyphens into spaces ("on-call" → "On Call"). This hits the public, indexable
 * job page, so it is a content bug, not just a cosmetic one.
 */
export function formatJobRequirement(requirement: string) {
  const value = requirement.trim();
  if (!value || !TAG_ENCODED_REQUIREMENT.test(value)) return requirement;
  return humanizeJobValue(value.split(':').pop() ?? value);
}

export function formatRelativeDate(value?: string | Date | null, locale = 'en') {
  if (!value) return '';
  const date = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(date.getTime())) return '';
  const diffMs = Date.now() - date.getTime();
  const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));
  const rtf = new Intl.RelativeTimeFormat(locale, { numeric: 'auto' });
  if (diffDays < 1) return rtf.format(0, 'day');
  if (diffDays < 30) return rtf.format(-diffDays, 'day');
  const diffMonths = Math.floor(diffDays / 30);
  if (diffMonths < 12) return rtf.format(-diffMonths, 'month');
  return new Intl.DateTimeFormat(locale, { month: 'short', day: 'numeric', year: 'numeric' }).format(date);
}

/**
 * Every block-level element carries `dir="auto"` rather than the wrapper: the
 * description is user-authored and routinely mixes scripts (an Arabic role
 * whose bullets quote English tool names, or the reverse). Without per-block
 * isolation the paragraph inherits the page direction and sentence-final
 * punctuation jumps to the wrong edge - ".focusing on motion planning and
 * control" instead of "…motion planning and control." on an Arabic page.
 */
export function JobDetailMarkdown({ value }: { value: string }) {
  const normalizedValue = normalizeJobDescriptionMarkdown(value);

  return (
    <div style={{ direction: 'inherit', textAlign: 'inherit' }}>
      <ReactMarkdown
        remarkPlugins={[remarkGfm]}
        components={{
          h1: ({ children }) => (
            <h1 dir="auto" style={{ margin: '0 0 10px', color: TEXT, fontSize: 28, lineHeight: '34px', fontWeight: 700, letterSpacing: 0 }}>
              {children}
            </h1>
          ),
          h2: ({ children }) => (
            <h2 dir="auto" style={{ margin: '0 0 10px', color: TEXT, fontSize: 24, lineHeight: '30px', fontWeight: 700, letterSpacing: 0 }}>
              {children}
            </h2>
          ),
          h3: ({ children }) => (
            <h3 dir="auto" style={{ margin: '0 0 8px', color: TEXT, fontSize: 20, lineHeight: '26px', fontWeight: 600, letterSpacing: 0 }}>
              {children}
            </h3>
          ),
          h4: ({ children }) => (
            <h4 dir="auto" style={{ margin: '0 0 8px', color: TEXT, fontSize: 18, lineHeight: '24px', fontWeight: 600 }}>
              {children}
            </h4>
          ),
          h5: ({ children }) => (
            <h5 dir="auto" style={{ margin: '0 0 8px', color: TEXT, fontSize: 16, lineHeight: '22px', fontWeight: 600 }}>
              {children}
            </h5>
          ),
          h6: ({ children }) => (
            <h6 dir="auto" style={{ margin: '0 0 8px', color: TEXT_SECONDARY, fontSize: 14, lineHeight: '20px', fontWeight: 600 }}>
              {children}
            </h6>
          ),
          p: ({ children }) => (
            <p dir="auto" style={{ margin: '0 0 12px', color: TEXT_SECONDARY, fontSize: 15, lineHeight: '24px', whiteSpace: 'pre-line' }}>
              {children}
            </p>
          ),
          strong: ({ children }) => <strong style={{ color: TEXT, fontWeight: 600 }}>{children}</strong>,
          em: ({ children }) => <em style={{ color: TEXT_SECONDARY, fontStyle: 'italic' }}>{children}</em>,
          a: ({ href, children }) => (
            <a href={href} target="_blank" rel="noreferrer" style={{ color: 'var(--tabbio-button-primary-bg, #5e29ff)', textDecoration: 'underline', fontWeight: 500 }}>
              {children}
            </a>
          ),
          blockquote: ({ children }) => (
            <blockquote dir="auto" style={{ margin: '0 0 12px', padding: '10px 12px', borderInlineStart: `3px solid ${BORDER}`, backgroundColor: SURFACE_SECONDARY, borderRadius: 14, color: TEXT_SECONDARY }}>
              {children}
            </blockquote>
          ),
          ul: ({ children }) => (
            <ul style={{ margin: '0 0 12px', paddingInlineStart: 20, color: TEXT_SECONDARY }}>
              {children}
            </ul>
          ),
          ol: ({ children }) => (
            <ol style={{ margin: '0 0 12px', paddingInlineStart: 20, color: TEXT_SECONDARY }}>
              {children}
            </ol>
          ),
          li: ({ children }) => (
            <li dir="auto" style={{ marginBottom: 6, fontSize: 15, lineHeight: '24px', color: TEXT_SECONDARY }}>
              {children}
            </li>
          ),
          hr: () => <hr style={{ border: 0, height: 1, backgroundColor: BORDER, margin: '12px 0' }} />,
          pre: ({ children }) => (
            <pre style={{ margin: '0 0 12px', padding: 12, borderRadius: 14, border: `1px solid ${BORDER}`, backgroundColor: SURFACE_SECONDARY, overflowX: 'auto', whiteSpace: 'pre-wrap' }}>
              {children}
            </pre>
          ),
          code: ({ children }) => (
            <code style={{ color: TEXT, backgroundColor: SURFACE_SECONDARY, borderRadius: 7, padding: '2px 6px', fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, Liberation Mono, monospace', fontSize: 14 }}>
              {children}
            </code>
          ),
          table: ({ children }) => (
            <div style={{ marginBottom: 12, overflowX: 'auto', border: `1px solid ${BORDER}`, borderRadius: 14, backgroundColor: SURFACE }}>
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>{children}</table>
            </div>
          ),
          th: ({ children }) => (
            <th dir="auto" style={{ padding: '10px 12px', textAlign: 'start', borderBottom: `1px solid ${BORDER}`, color: TEXT, backgroundColor: SURFACE, fontSize: 14, fontWeight: 600 }}>
              {children}
            </th>
          ),
          td: ({ children }) => (
            <td dir="auto" style={{ padding: '10px 12px', textAlign: 'start', borderTop: `1px solid ${BORDER}`, color: TEXT_SECONDARY, fontSize: 14, lineHeight: '22px', backgroundColor: SURFACE }}>
              {children}
            </td>
          ),
          img: ({ src, alt }) => (
            <img src={src ?? ''} alt={alt ?? ''} style={{ width: '100%', minHeight: 180, objectFit: 'cover', borderRadius: 18, marginBottom: 12 }} />
          ),
        }}
      >
        {normalizedValue}
      </ReactMarkdown>
    </div>
  );
}

function DetailPanel({ title, children }: { title: string; children: ReactNode }) {
  return (
    <section style={{ marginTop: 22 }}>
      <h3 dir="auto" style={{ margin: '0 0 10px', fontSize: 16, fontWeight: 600, color: TEXT }}>{title}</h3>
      <div
        style={{
          borderRadius: 28,
          border: `1px solid ${BORDER_SUBTLE}`,
          backgroundColor: SURFACE_ELEVATED,
          padding: 18,
        }}
      >
        {children}
      </div>
    </section>
  );
}

export function JobDetailSurface({
  job,
  labels: labelOverrides,
  statusSlot,
  matchSlot,
  actionSlot,
  afterActionSlot,
  maxWidth = 760,
  className,
  style,
}: JobDetailSurfaceProps) {
  const labels = { ...DEFAULT_LABELS, ...labelOverrides };
  const metaItems = [job.type, job.workMode, job.level].filter(Boolean) as string[];
  const screeningQuestions = job.screeningQuestions ?? [];
  const tags = job.tags ?? [];
  const companyLogo = job.company.logo?.trim() || null;
  const companyHref = job.company.href?.trim() || null;

  return (
    <div
      className={className}
      style={{
        maxWidth,
        marginInline: 'auto',
        width: '100%',
        paddingInline: 'var(--tabbio-job-detail-gutter, 16px)',
        ...style,
      }}
    >
      <section
        style={{
          borderRadius: 24,
          border: `1px solid ${BORDER_SUBTLE}`,
          backgroundColor: SURFACE_ELEVATED,
          padding: 22,
          boxShadow: 'none',
        }}
      >
        <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 16, marginBottom: 20 }}>
          <div
            aria-hidden="true"
            style={{
              width: 64,
              height: 64,
              borderRadius: 16,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              flexShrink: 0,
              overflow: 'hidden',
              border: `1px solid ${BORDER_SUBTLE}`,
              backgroundColor: SURFACE,
              color: TEXT_TERTIARY,
            }}
          >
            {companyLogo ? (
              <img
                src={companyLogo}
                alt={job.company.name}
                style={{ width: 42, height: 42, objectFit: 'contain' }}
              />
            ) : (
              <JobSuitcaseIcon size={28} />
            )}
          </div>

          {job.postedLabel ? (
            <div style={{ fontSize: 13, fontWeight: 500, color: TEXT_TERTIARY }}>{job.postedLabel}</div>
          ) : null}
        </div>

        <h1 dir="auto" style={{ margin: 0, fontSize: 32, lineHeight: '38px', color: TEXT, fontWeight: 700, letterSpacing: 0, overflowWrap: 'anywhere' }}>
          {job.title}
        </h1>

        <div dir="auto" style={{ marginTop: 8, fontSize: 15, fontWeight: 500, color: TEXT_SECONDARY, overflowWrap: 'anywhere' }}>
          {companyHref ? (
            <a
              href={companyHref}
              style={{
                color: 'inherit',
                textDecoration: 'underline',
                textDecorationColor: TEXT_TERTIARY,
                textUnderlineOffset: 3,
              }}
            >
              {job.company.name}
            </a>
          ) : (
            job.company.name
          )}
          {' · '}
          {job.location || labels.locationFallback}
        </div>

        {(job.sourceLabel || job.applyModeLabel) ? (
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginTop: 12 }}>
            {job.sourceLabel ? (
              <span
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: 6,
                  borderRadius: 999,
                  padding: '6px 10px',
                  border: `1px solid ${BORDER_SUBTLE}`,
                  backgroundColor: SURFACE,
                  color: TEXT_SECONDARY,
                  fontSize: 12,
                  fontWeight: 600,
                }}
              >
                {job.sourceIcon}
                {job.sourceLabel}
              </span>
            ) : null}
            {job.applyModeLabel ? (
              <span
                style={{
                  borderRadius: 999,
                  padding: '6px 10px',
                  border: `1px solid ${BORDER_SUBTLE}`,
                  backgroundColor: SURFACE_SECONDARY,
                  color: TEXT_SECONDARY,
                  fontSize: 12,
                  fontWeight: 600,
                }}
              >
                {job.applyModeLabel}
              </span>
            ) : null}
          </div>
        ) : null}

        {metaItems.length > 0 ? (
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginTop: 18 }}>
            {metaItems.map((item) => (
              <div
                key={item}
                dir="auto"
                style={{
                  borderRadius: 999,
                  padding: '8px 12px',
                  border: `1px solid ${BORDER_SUBTLE}`,
                  backgroundColor: SURFACE,
                  color: TEXT_SECONDARY,
                  fontSize: 13,
                  fontWeight: 500,
                }}
              >
                {humanizeJobValue(item)}
              </div>
            ))}
          </div>
        ) : null}

        {statusSlot}
        {matchSlot}
      </section>

      <DetailPanel title={labels.description}>
        <JobDetailMarkdown value={job.description} />
      </DetailPanel>

      {job.requirements.length > 0 ? (
        <DetailPanel title={labels.requirements}>
          {job.requirements.map((requirement, index) => (
            <div
              key={`${requirement}-${index}`}
              style={{
                display: 'flex',
                alignItems: 'flex-start',
                gap: 12,
                paddingBlock: 12,
                borderBottom: index === job.requirements.length - 1 ? 'none' : `1px solid ${BORDER_SUBTLE}`,
              }}
            >
              <span style={{ width: 24, fontSize: 12, fontWeight: 700, lineHeight: '20px', color: TEXT_TERTIARY, flexShrink: 0 }}>
                {index + 1}
              </span>
              <span dir="auto" style={{ fontSize: 15, lineHeight: '22px', color: TEXT_SECONDARY }}>
                {formatJobRequirement(requirement)}
              </span>
            </div>
          ))}
        </DetailPanel>
      ) : null}

      {screeningQuestions.length > 0 ? (
        <DetailPanel title={labels.screeningQuestions}>
          {screeningQuestions.map((question, index) => (
            <div
              key={question.id}
              style={{
                display: 'flex',
                alignItems: 'flex-start',
                gap: 12,
                paddingBlock: 12,
                borderBottom: index === screeningQuestions.length - 1 ? 'none' : `1px solid ${BORDER_SUBTLE}`,
              }}
            >
              <span style={{ width: 24, fontSize: 12, fontWeight: 700, lineHeight: '20px', color: TEXT_TERTIARY, flexShrink: 0 }}>
                Q{index + 1}
              </span>
              <span dir="auto" style={{ fontSize: 15, lineHeight: '22px', color: TEXT }}>
                {question.prompt}
              </span>
            </div>
          ))}
        </DetailPanel>
      ) : null}

      {tags.length > 0 ? (
        <DetailPanel title={labels.tags}>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
            {tags.map((tag) => (
              <span
                key={tag}
                dir="auto"
                style={{
                  borderRadius: 999,
                  padding: '7px 11px',
                  border: `1px solid ${BORDER_SUBTLE}`,
                  backgroundColor: SURFACE,
                  color: TEXT_SECONDARY,
                  fontSize: 13,
                  fontWeight: 500,
                }}
              >
                {tag}
              </span>
            ))}
          </div>
        </DetailPanel>
      ) : null}

      {actionSlot || afterActionSlot ? (
        <div style={{ marginTop: 24, display: 'grid', gap: 8 }}>
          {actionSlot}
          {afterActionSlot}
        </div>
      ) : null}
    </div>
  );
}
